import { useState, useEffect, useRef, type FormEvent } from "react"
import { ChevronDown } from "lucide-react"
import styles from "./ChatHero.module.css"

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

const SUGGESTED_QUESTIONS = [
  "What's his stack?",
  "What's he looking for?",
  "Show me a project",
  "Where's he based?",
]

const TYPEWRITER_SPEED = 25 // ms per step
const CHARS_PER_STEP = 2   // characters revealed per step

export function ChatHero() {
  const [input, setInput] = useState("")
  const [response, setResponse] = useState("")
  const [displayed, setDisplayed] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showScrollCue, setShowScrollCue] = useState(true)
  const heroRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = heroRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => setShowScrollCue(entries[0]?.isIntersecting ?? true),
      { threshold: 0.2 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!response) {
      setDisplayed("")
      return
    }
    setIsTyping(true)
    setDisplayed("")
    let i = 0
    const interval = setInterval(() => {
      i += CHARS_PER_STEP
      setDisplayed(response.slice(0, i))
      if (i >= response.length) {
        setDisplayed(response)
        setIsTyping(false)
        clearInterval(interval)
      }
    }, TYPEWRITER_SPEED)
    return () => clearInterval(interval)
  }, [response])

  const ask = async (question: string) => {
    setIsLoading(true)
    setError("")
    setResponse("")
    setDisplayed("")

    window.gtag?.("event", "chat_question", {
      question_text: question.slice(0, 100),
      is_suggested: SUGGESTED_QUESTIONS.includes(question),
    })

    try {
      const res = await fetch("/.netlify/functions/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: question }),
      })

      if (!res.ok) throw new Error("Request failed")

      const data: { response: string } = await res.json()
      setResponse(data.response)
    } catch {
      setError("Something went wrong. Try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return
    ask(input.trim())
    setInput("")
  }

  return (
    <div className={styles.hero} ref={heroRef}>
      <h1 className={styles.name}>Evan King</h1>
      <p className={styles.subtitle}>Full-Stack Engineer</p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          className={styles.input}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask anything about Evan"
          disabled={isLoading}
          autoFocus
        />
        <button
          className={styles.submit}
          type="submit"
          disabled={isLoading || !input.trim()}
          aria-label="Ask"
        >
          {isLoading ? "…" : "→"}
        </button>
      </form>

      <p className={styles.pillsLabel}>Or try one of these?</p>

      <div className={styles.pills}>
        {SUGGESTED_QUESTIONS.map((q, i) => (
          <button
            key={q}
            type="button"
            className={styles.pill}
            style={{ animationDelay: `${i * 60}ms` }}
            onClick={() => ask(q)}
            disabled={isLoading}
          >
            {q}
          </button>
        ))}
      </div>

      {isLoading && <p className={styles.loading}>Thinking…</p>}
      {error && <p className={styles.error}>{error}</p>}
      {displayed && (
        <p className={styles.response}>
          {displayed}
          {isTyping && <span className={styles.cursor} aria-hidden="true">▋</span>}
        </p>
      )}

      <a
        href="#highlights"
        className={`${styles.scrollCue} ${showScrollCue ? "" : styles.scrollCueHidden}`}
        aria-label="Scroll down for more"
      >
        <ChevronDown size={26} />
      </a>
    </div>
  )
}
